import { useState, useEffect, FormEvent } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import LogoutIcon from '@mui/icons-material/Logout';
import { auth } from '../firebase/firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  User
} from 'firebase/auth';

/**
 * Authentication Component
 * 
 * Handles user authentication with Firebase using Material-UI
 */
const Authentication: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isSignUp, setIsSignUp] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    // Clean up subscription
    return () => unsubscribe();
  }, []);

  // Handle email/password sign in
  const handleEmailSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        // Create a new user
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        // Sign in existing user
        await signInWithEmailAndPassword(auth, email, password);
      }
      
      // Clear form
      setEmail('');
      setPassword('');
    } catch (err: any) {
      console.error('Authentication error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Google sign in
  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      console.error('Google sign in error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    setError(null);
    setLoading(true);

    try {
      await signOut(auth);
    } catch (err: any) {
      console.error('Sign out error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // If user is signed in, show user info and sign out button
  if (user) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body1">
              Signed in as: {user.displayName || user.email}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSignOut}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <LogoutIcon />}
            >
              {loading ? 'Signing Out...' : 'Sign Out'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // If no user, show sign in form
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom align="center">
          {isSignUp ? 'Create Account' : 'Sign In'}
        </Typography>
        
        {error && (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        )}
        
        <Stack component="form" onSubmit={handleEmailSignIn} spacing={2}>
          <TextField
            fullWidth
            type="email"
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            fullWidth
            type="password"
            label="Password"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : isSignUp ? 'Sign Up' : 'Sign In'}
          </Button>
        </Stack>
        
        <Stack direction="row" alignItems="center" spacing={2} marginY={2}>
          <Divider flexItem />
          <Typography variant="body2">OR</Typography>
          <Divider flexItem />
        </Stack>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleGoogleSignIn}
          disabled={loading}
          startIcon={<GoogleIcon />}
          fullWidth
        >
          Sign in with Google
        </Button>
        
        <Box textAlign="center" marginTop={2}>
          <Button
            variant="text"
            onClick={() => setIsSignUp(!isSignUp)}
            disabled={loading}
          >
            {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Authentication;